import React from 'react';
import { Signal } from '@/api/types';
import { SignalPriceInfo } from '@/components/common';
import { formatSignalValue } from '@/utils/formatting';
import { SIGNAL_TYPE_LABELS, DIRECTION_LABELS } from '@/api/types';
import { useTelegram } from '@/hooks';

interface SignalCardProps {
  signal: Signal;
  currentPrice?: number;
  onClick?: () => void;
}

export const SignalCard: React.FC<SignalCardProps> = ({ signal, currentPrice, onClick }) => {
  const { impactOccurred } = useTelegram();

  const handleClick = () => {
    impactOccurred('light');
    onClick?.();
  };

  return (
    <div
      onClick={handleClick}
      style={{
        backgroundColor: 'var(--tg-theme-secondary-bg-color)',
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '12px',
        cursor: onClick ? 'pointer' : 'default',
        opacity: signal.active ? 1 : 0.6,
        transition: 'transform 0.1s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Left: Symbol and info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            {signal.asset?.image && (
              <img
                src={signal.asset.image}
                alt={signal.symbol}
                style={{ width: '24px', height: '24px', borderRadius: '50%' }}
              />
            )}
            <span
              style={{
                fontSize: '17px',
                fontWeight: 600,
                color: 'var(--tg-theme-text-color)',
              }}
            >
              {signal.symbol}/{signal.quoteCurrency}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '8px',
            }}
          >
            <span
              style={{
                fontSize: '12px',
                padding: '3px 8px',
                borderRadius: '6px',
                backgroundColor: 'var(--tg-theme-button-color)',
                color: 'var(--tg-theme-button-text-color)',
              }}
            >
              {SIGNAL_TYPE_LABELS[signal.type]}
            </span>
            {signal.direction !== 'none' && (
              <span
                style={{
                  fontSize: '12px',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  backgroundColor: signal.direction === 'above' ? 'rgba(52, 199, 89, 0.15)' : 'rgba(255, 59, 48, 0.15)',
                  color: signal.direction === 'above' ? '#34c759' : '#ff3b30',
                }}
              >
                {DIRECTION_LABELS[signal.direction]}
              </span>
            )}
          </div>

          <div
            style={{
              marginTop: '8px',
              fontSize: '14px',
              color: 'var(--tg-theme-hint-color)',
            }}
          >
            Target: {formatSignalValue(signal.value, signal.type, signal.quoteCurrency)}
          </div>
        </div>

        {/* Right: Price info */}
        <SignalPriceInfo
          startPrice={signal.startPrice}
          currentPrice={currentPrice}
          currency={signal.quoteCurrency}
        />
      </div>

      {/* Status indicators */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        {signal.isTriggered && (
          <span
            style={{
              fontSize: '11px',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: 'rgba(255, 149, 0, 0.15)',
              color: '#ff9500',
            }}
          >
            Triggered
          </span>
        )}
        {signal.repeat && (
          <span
            style={{
              fontSize: '11px',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: 'rgba(52, 199, 89, 0.15)',
              color: '#34c759',
            }}
          >
            Repeat
          </span>
        )}
        {!signal.active && (
          <span
            style={{
              fontSize: '11px',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: 'rgba(142, 142, 147, 0.15)',
              color: '#8e8e93',
            }}
          >
            Paused
          </span>
        )}
      </div>
    </div>
  );
};
